import React from 'react';
import { styled, Theme } from '@mui/material/styles';

/**
 * Interface defining the possible style properties for the component.
 */
export interface WithStyleProps {
  /** Optional inline styles for the component */
  style?: React.CSSProperties;
}

/**
 * Interface defining the props that will be passed to the callback function.
 * This interface extends the component props with the theme object.
 */
export type CallbackProps<T> = T & { theme: Theme };

/**
 * Helper function to add `!important` to style properties.
 *
 * This function iterates over the provided styles and appends `!important` to each property value.
 *
 * @param styles - The styles to which `!important` should be added.
 * @returns The styles with `!important` appended.
 *
 * @example
 * const styles = { color: 'red' };
 * const importantStyles = addImportant(styles);
 * // { color: 'red !important' }
 */
const addImportant = (
  styles: Record<string, string | number> = {},
): Record<string, string> => {
  return Object.fromEntries(
    Object.entries(styles).map(([key, value]) => [
      key,
      `${value} !important`, // Append `!important` to each style value
    ]),
  );
};

/**
 * Higher-order function that returns a styled version of the provided component.
 * This HOC allows you to:
 * - Filter out props that should not be forwarded to the base component.
 * - Apply dynamic style changes through mixins.
 *
 * The `mixins` function is used to generate additional styles based on the component props.
 * The `styled` function from MUI is used to create a new component with dynamic styles.
 *
 * @param Component - The component to be styled and wrapped.
 * @param options - Configuration options:
 *   - `ignoreProps`: An optional array of prop names that should not be forwarded to the wrapped component. These props will be filtered out before being passed to the base component.
 *   - `mixins`: An optional function that returns additional styles based on the component props. This function is called with the component props and should return an object with style properties.
 *
 * @returns A new component that is styled with the provided styles and has the filtered props forwarded. The resulting component will apply the styles dynamically based on its props and theme.
 *
 * @example
 * const StyledButton = styledComponent(Button, {
 *   ignoreProps: ['style'],
 *   mixins: (props) => ({
 *     backgroundColor: props.isPrimary ? 'blue' : 'gray',
 *   }),
 * });
 *
 * // Use the styled component
 * <StyledButton isPrimary={true} />
 *
 * @document StyledComponentHOCUsage.md
 * @category Theme and Styling
 */
export const styledComponent = <ExtendedComponentProps extends WithStyleProps>(
  Component: React.ElementType,
  options?: {
    /** Props that should be ignored and not forwarded to the component */
    ignoreProps?: string[];
    /** Function to add dynamic styles based on component props */
    mixins?: (
      props: ExtendedComponentProps,
    ) => Record<string, string | number> | undefined;
  },
) => {
  const ComponentCreator = styled((props: ExtendedComponentProps) => {
    // Filter out props listed in ignoreProps
    const filteredProps = Object.fromEntries(
      Object.entries(props).filter(
        ([key]) => !options?.ignoreProps?.includes(key),
      ),
    );

    // Return the component with the filtered props
    return <Component {...(filteredProps as ExtendedComponentProps)} />;
  });

  return (
    callback: (
      props: CallbackProps<ExtendedComponentProps>,
    ) => React.CSSProperties,
  ) =>
    ComponentCreator((props: { theme: Theme }) => {
      const componentProps = props as CallbackProps<ExtendedComponentProps>;
      // Apply mixins function if provided and add !important
      const mixinStyles = options?.mixins
        ? addImportant(options.mixins(componentProps))
        : {};

      // Return styles from the callback and mixins
      return {
        ...callback(componentProps),
        ...mixinStyles,
      };
    });
};
